import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DeviceMobile, Desktop, DeviceTablet, SignOut, ShieldCheck } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const iconFor = (t) => (t === "Mobile" ? DeviceMobile : t === "Tablet" ? DeviceTablet : Desktop);
const fmt = (s) =>
  s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function Sessions() {
  const [list, setList] = useState(null);
  const load = () => api.get("/auth/sessions").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    try { await api.post(`/auth/sessions/${id}/revoke`); toast.success("Device signed out"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const revokeOthers = async () => {
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(`Signed out ${data.revoked} other device${data.revoked === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!list) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="sessions-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">
            <ShieldCheck size={26} weight="duotone" className="text-primary" /> Devices &amp; sessions
          </h1>
          <p className="text-muted-foreground text-sm">Manage where you're signed in. Sign out any device you don't recognise.</p>
        </div>
        {list.length > 1 && (
          <Button data-testid="revoke-others-btn" onClick={revokeOthers} variant="outline" className="rounded-full border-black/10">
            Sign out all other devices
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {list.map((s) => {
          const Icon = iconFor(s.device_type);
          return (
            <div key={s.id} data-testid={`session-${s.id}`} className="card-surface p-4 sm:p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                <Icon size={22} weight="duotone" className="text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display font-semibold text-sm flex items-center gap-2 flex-wrap">
                  {s.device_type} · {s.browser} on {s.os}
                  {s.current && (
                    <span data-testid="current-badge" className="text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">
                      This device
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {s.location || s.ip || "Unknown location"} · Last active {fmt(s.last_active_at)} · Signed in {fmt(s.created_at)}
                </p>
              </div>
              {!s.current && (
                <button data-testid={`revoke-${s.id}`} onClick={() => revoke(s.id)} title="Sign out this device"
                  className="text-muted-foreground hover:text-red-600 p-2 shrink-0 transition-colors">
                  <SignOut size={20} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
